import { useRef, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import styles from "./ModalHeader.module.css";
import { useLogoSrc } from "../context/LogoContext";
import { useFocusTrap } from "../hooks/useFocusTrap";
import { useTheme } from "../hooks/useTheme";

const DEFAULT_LOGO_DARK = "/brand/treinonexo-wordmark-white.svg";
const DEFAULT_LOGO_LIGHT = "/brand/treinonexo-wordmark.svg";

/**
 * Branded modal header — logo left, close button right.
 *
 * Ativa focus trap e handler de Escape automaticamente no modal container
 * (parentElement deste header), sem necessidade de mudanças nos modais.
 *
 * Props:
 *   onClose   – () => void
 *   disabled  – bool (disables the close button, e.g. during async ops)
 */
export default function ModalHeader({ onClose, disabled = false }) {
  const { dark: ctxLogoDark, light: ctxLogoLight } = useLogoSrc() || {};
  const hasCustomLogo = !!(ctxLogoDark || ctxLogoLight);
  const { isLight } = useTheme();
  const defaultLogo = isLight ? DEFAULT_LOGO_LIGHT : DEFAULT_LOGO_DARK;
  // Mesma troca dark/light do wordmark padrão acima (R248) — a variante
  // clara é OPCIONAL: sem ela (professor só enviou um logo, comportamento de
  // sempre), a dark vale pros dois modos, igual antes dessa variante existir.
  const customLogoSrc = isLight
    ? (ctxLogoLight || ctxLogoDark)
    : (ctxLogoDark || ctxLogoLight);
  const logoSrc   = hasCustomLogo ? customLogoSrc : defaultLogo;
  const headerRef = useRef(null);

  // useMemo: referencia estavel para containerRef.
  // Sem isso, novo objeto a cada render dispara o effect do useFocusTrap
  // e rouba o foco do input a cada keystroke.
  const containerRef = useMemo(() => ({
    get current() { return headerRef.current?.parentElement ?? null; },
  }), []);

  useFocusTrap(containerRef, true, disabled ? undefined : onClose);

  // .headerAccent: sempre ligado — --accent-rgb já resolve pra cor do
  // professor, cor própria do aluno self-service (R36) ou teal institucional
  // como piso, então sempre há alguma cor de marca em vigor. .headerBranded:
  // só quando existe logo-imagem de verdade (exclusivo de professor) — segue
  // o fundo customizado do professor (R247), não fica mais fixo em escuro.
  // Sem placa/reforço adicional (tentado e revertido em R248 — "não ficou
  // legal"): o risco de contraste aceito em R247 agora tem uma saída de
  // verdade pro professor (variante clara opcional), igual o resto da
  // interface se autocorrige — não precisa de mais nenhum mecanismo.
  const headerClass = [
    styles.header,
    styles.headerAccent,
    hasCustomLogo ? styles.headerBranded : "",
  ].filter(Boolean).join(" ");

  return (
    <div className={headerClass} ref={headerRef}>
      <img
        src={logoSrc}
        alt="Logo"
        className={hasCustomLogo ? styles.logoCustom : styles.logo}
        draggable={false}
      />
      <button
        type="button"
        className={styles.closeBtn}
        onClick={onClose}
        disabled={disabled}
        aria-label="Fechar"
      >
        <FontAwesomeIcon icon={faXmark} />
      </button>
    </div>
  );
}
